import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useLocale } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useId } from 'react';
import {
  ACCOUNT_NOTE,
  acknowledgmentStatement,
  firstRenewalMonths,
  HIDE_LOWER_TIERS_LINK,
  LOWER_TIERS_LINK,
  PRICING_NOTE,
  splitTiers,
  steps,
  TIER_HELP,
} from './helpers/acknowledgmentCopy';
import { isSubscriptionProduct } from './helpers/disclosureCopy';
import SubscriptionDisclosureTiers from './SubscriptionDisclosureTiers';

// The subscription a product turns into by default: a plan points at its
// planSubscription; a subscription bought directly is itself.
function defaultTierIdFor(product) {
  return isSubscriptionProduct(product)
    ? product?.id
    : product?.planSubscription?.id;
}

const FIELD_PATHS = ['agreed', 'selectedSubscriptionProductId'];

// Disclosure body for ONE plan or subscription product, shared by the
// checkout modal, the onboarding step and the dashboard gate.
//
// A plan shows only the tier it becomes by default; every other tier sits
// behind the "Additional plans" link (see splitTiers). Whether the customer
// opened the link is part of the submission, so the ledger records it.
function SubscriptionDisclosure({
  product,
  tiers,
  submission,
  onChange,
  errorMessages = null,
}) {
  const { locale } = useLocale();
  const agreeInputId = useId();

  const directSubscription = isSubscriptionProduct(product);
  const allowTierSelection = !directSubscription;
  const defaultTierId = defaultTierIdFor(product);
  const tierById = new Map(tiers.map((tier) => [tier.product.id, tier]));
  const selectedTier = submission.selectedSubscriptionProductId
    ? tierById.get(submission.selectedSubscriptionProductId)
    : null;
  const effectiveTier = selectedTier || tierById.get(defaultTierId) || null;

  const { upFront: upFrontTiers, additional: additionalTiers } = splitTiers({
    product,
    tiers,
  });
  // A tier chosen from the additional list keeps the list open, so the
  // selection is never hidden.
  const selectedIsAdditional = additionalTiers.some(
    (tier) => tier.product.id === submission.selectedSubscriptionProductId,
  );
  const additionalTiersShown =
    additionalTiers.length > 0 &&
    (submission.lowerTiersExpanded || selectedIsAdditional);

  const cadenceMonths =
    effectiveTier?.product?.renewalIntervalMonths ??
    product?.planSubscription?.renewalIntervalMonths;
  const copy = {
    planName: product?.name || 'plan',
    subscription: directSubscription,
    tierName: effectiveTier?.product?.name || product?.planSubscription?.name,
    cadenceMonths,
    firstRenewalMonths: firstRenewalMonths({
      planStartInterval: product?.planStartInterval,
      cadenceMonths,
    }),
  };
  const priceCents = effectiveTier?.price ?? product?.planSubscription?.price;

  function update(changes) {
    onChange({ ...submission, ...changes });
  }

  function handleSelect(productId) {
    update({
      selectedSubscriptionProductId:
        submission.selectedSubscriptionProductId === productId
          ? null
          : productId,
    });
  }

  const errorFor = (path) =>
    errorMessages?.filter((message) => message.path === path) || [];
  const generalErrors =
    errorMessages?.filter((message) => !FIELD_PATHS.includes(message.path)) ||
    [];

  return (
    <div className="flex flex-col gap-6">
      <ol className="grid gap-3 sm:grid-cols-3">
        {steps(copy).map((step, index) => (
          <li
            key={step.title}
            className="flex flex-col gap-2 rounded-xl border border-gray-200 p-4"
          >
            <span className="flex size-7 items-center justify-center rounded-full bg-rcprimary-400 font-semibold text-gray-950 text-sm">
              {index + 1}
            </span>
            <span className="font-semibold text-gray-950">{step.title}</span>
            <span className="text-gray-700 text-sm leading-6">{step.body}</span>
          </li>
        ))}
      </ol>

      {allowTierSelection && (
        <p className="rounded-md bg-rcprimary-50 px-4 py-3 text-gray-900 text-sm">
          {TIER_HELP}
        </p>
      )}

      <SubscriptionDisclosureTiers
        tiers={upFrontTiers}
        defaultTierIds={defaultTierId ? [defaultTierId] : []}
        selectedTierId={submission.selectedSubscriptionProductId}
        allowSelection={allowTierSelection}
        onSelect={handleSelect}
        locale={locale}
        defaultBadge={directSubscription ? 'Your Subscription' : 'Recommended'}
      />

      {additionalTiers.length > 0 && (
        <div className="flex flex-col gap-4">
          <button
            type="button"
            aria-expanded={additionalTiersShown}
            onClick={() =>
              update({ lowerTiersExpanded: !submission.lowerTiersExpanded })
            }
            className="self-start font-semibold text-rcprimary-500 text-sm underline focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2"
          >
            {additionalTiersShown ? HIDE_LOWER_TIERS_LINK : LOWER_TIERS_LINK}
          </button>
          {additionalTiersShown && (
            <SubscriptionDisclosureTiers
              tiers={additionalTiers}
              defaultTierIds={[]}
              selectedTierId={submission.selectedSubscriptionProductId}
              allowSelection={allowTierSelection}
              onSelect={handleSelect}
              locale={locale}
            />
          )}
        </div>
      )}
      {errorFor('selectedSubscriptionProductId').map((message) => (
        <p key={message.message} className="text-red-700 text-sm">
          {message.message}
        </p>
      ))}

      <div className="flex items-start gap-3 rounded-xl border border-rcprimary-400/50 bg-rcprimary-50 p-4 text-gray-900 text-sm leading-6">
        <InformationCircleIcon
          aria-hidden
          className="mt-0.5 size-5 shrink-0 text-rcprimary-500"
        />
        <div className="flex flex-col gap-2">
          <p>{PRICING_NOTE}</p>
          <p>{ACCOUNT_NOTE}</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-gray-200 border-t pt-6">
        <label
          htmlFor={agreeInputId}
          className="flex cursor-pointer items-start gap-x-3 text-gray-900 text-sm leading-6"
        >
          <input
            id={agreeInputId}
            type="checkbox"
            checked={submission.agreed}
            onChange={(event) => update({ agreed: event.target.checked })}
            className="mt-0.5 size-5 rounded border-gray-400 text-rcprimary-400 focus:ring-rcprimary-400"
          />
          <span>{acknowledgmentStatement({ ...copy, priceCents, locale })}</span>
        </label>
        {errorFor('agreed').map((message) => (
          <p key={message.message} className="text-red-700 text-sm">
            {message.message}
          </p>
        ))}
      </div>

      {generalErrors.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-lg border border-red-500 bg-red-50 p-3 text-red-800 text-sm"
          role="alert"
        >
          <ExclamationTriangleIcon
            aria-hidden
            className="size-5 shrink-0 text-red-600"
          />
          <div>
            {generalErrors.map((message) => (
              <p key={message.message}>{message.message}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

SubscriptionDisclosure.propTypes = {
  product: PropTypes.object.isRequired,
  tiers: PropTypes.array.isRequired,
  submission: PropTypes.shape({
    agreed: PropTypes.bool.isRequired,
    selectedSubscriptionProductId: PropTypes.string,
    lowerTiersExpanded: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  errorMessages: PropTypes.array,
};

export default SubscriptionDisclosure;
